import { resolveAssetUrl } from './rpc';

export interface SiteBranding {
  BRANDING_NAME?: string;
  BRANDING_LOGO?: string;
  BRANDING_LOGO_LIGHT?: string;
  BRANDING_FAVICON?: string;
  BRANDING_COLOR?: string;
  BRANDING_LOGIN_TEXT?: string;
}

let cachedBranding: SiteBranding | null = null;

/**
 * Dynamically swaps or resets favicon and apple-touch-icon links in the document head.
 * Sets the exact MIME type and cache-buster query parameter to bypass aggressive browser caching.
 */
export function applyFavicon(faviconUrl?: string) {
  if (typeof document === 'undefined') return;

  const resolved = faviconUrl ? resolveAssetUrl(faviconUrl) : '';

  // Remove existing dynamic and static icon tags to ensure clean swap
  const existingIcons = document.querySelectorAll("link[rel*='icon']");
  existingIcons.forEach(el => el.remove());

  const ts = Date.now();

  if (resolved) {
    const sep = resolved.includes('?') ? '&' : '?';
    const cacheBusted = `${resolved}${sep}v=${ts}`;
    const ext = resolved.split('.').pop()?.split('?')[0]?.toLowerCase();

    let mimeType = 'image/png';
    if (ext === 'svg') mimeType = 'image/svg+xml';
    else if (ext === 'ico') mimeType = 'image/x-icon';
    else if (ext === 'webp') mimeType = 'image/webp';
    else if (ext === 'jpg' || ext === 'jpeg') mimeType = 'image/jpeg';

    const link = document.createElement('link');
    link.rel = 'icon';
    link.type = mimeType;
    link.href = cacheBusted;
    document.head.appendChild(link);

    const appleLink = document.createElement('link');
    appleLink.rel = 'apple-touch-icon';
    appleLink.href = cacheBusted;
    document.head.appendChild(appleLink);
  } else {
    // Re-apply official default icons with cache-busting timestamp
    const svgLink = document.createElement('link');
    svgLink.rel = 'icon';
    svgLink.type = 'image/svg+xml';
    svgLink.href = `/favicon.svg?v=${ts}`;
    document.head.appendChild(svgLink);

    const icoLink = document.createElement('link');
    icoLink.rel = 'icon';
    icoLink.type = 'image/x-icon';
    icoLink.href = `/favicon.ico?v=${ts}`;
    document.head.appendChild(icoLink);

    const appleLink = document.createElement('link');
    appleLink.rel = 'apple-touch-icon';
    appleLink.href = `/icon-192.png?v=${ts}`;
    document.head.appendChild(appleLink);
  }
}

export function applySiteTheme(branding: SiteBranding) {
  if (typeof document === 'undefined') return;

  if (branding.BRANDING_FAVICON) {
    applyFavicon(branding.BRANDING_FAVICON);
  } else {
    applyFavicon();
  }

  if (branding.BRANDING_COLOR) {
    document.documentElement.style.setProperty('--primary', branding.BRANDING_COLOR);
    const metaTheme = document.querySelector('meta[name="theme-color"]');
    if (metaTheme) metaTheme.setAttribute('content', branding.BRANDING_COLOR);
  }
}

export async function fetchSiteBranding(): Promise<SiteBranding> {
  try {
    const res = await fetch('/api/public-settings');
    if (res.ok) {
      const data = await res.json();
      cachedBranding = data;
      applySiteTheme(data);
      return data;
    }
  } catch (err) {
    console.error('Failed to fetch site branding:', err);
  }
  return {};
}

export function getCachedBranding(): SiteBranding | null {
  return cachedBranding;
}
